import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { autoFitColumns, addAutoFilter } from '../../utils/excel-export.util';
import { OutingService, OutingFilters } from '../../services/outing.service';
import { ProjectService } from '../../services/project.service';
import { EmployeeService } from '../../services/employee.service';
import { Employee } from '../../models/employee.model';
import { Project } from '../../models/project.model';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import { Outing, OutingStatus, OutingReportSummary } from '../../models/outing.model';

interface DropdownOption {
  label: string;
  value: string | number | null;
}

// One grid row: an employee (or the Total rollup) with a per-month count across the selected year.
// byMonth is keyed 1-12 so the template can loop the month columns.
interface OutingReportRow {
  rowType: 'employee' | 'total';
  employeeId: string;
  employeeName: string;
  serviceGroupId?: string;
  serviceGroupName?: string;
  byMonth: Record<number, number>;
  yearTotal: number;
}

// Per-month headline figures, surfaced in the month-header tooltips.
interface MonthSummary {
  month: number;
  label: string;
  outingCount: number;
  invited: number;
  participated: number;
  absent: number;
  budget: number;
  spend: number;
  participationRate: number;
}

// Team Outing Report — a 12-month view of outing participation, structured like the Availability
// Report (stat cards, then a Jan–Dec grid keyed by employee). Read-only: Manage Outings and Outing
// Summary remain the editable/detail views, which this page deliberately does not duplicate.
@Component({
  selector: 'app-outing-report',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, Select, Tooltip],
  providers: [MessageService],
  templateUrl: './outing-report.component.html',
  styleUrl: './outing-report.component.scss'
})
export class OutingReportComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  readonly monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  readonly months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  outings: Outing[] = [];
  report: OutingReportSummary | null = null;
  employees: Employee[] = [];
  projects: Project[] = [];
  loading = false;

  // Server filters
  selectedYear: number = new Date().getFullYear();
  filterProjectId: string | null = null;
  // Client filters
  filterServiceGroupId: string | null = null;
  // participated = recorded attendance only; invited = everyone on the invite list.
  countMode: 'participated' | 'invited' = 'participated';
  nameSearch = '';

  yearOptions: DropdownOption[] = [];
  projectFilterOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [{ label: 'All service groups', value: null }];
  countModeOptions: DropdownOption[] = [
    { label: 'Participated', value: 'participated' },
    { label: 'Invited', value: 'invited' }
  ];

  constructor(
    private outingService: OutingService,
    private projectService: ProjectService,
    private employeeService: EmployeeService,
    private messageService: MessageService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit(): void {
    // The main header drives employee search, the funnel toggle and Export, so this page needs no
    // in-page search box of its own (same wiring as the other report pages).
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search employee…');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(false);
    this.nameSearch = this.pageHeaderService.currentSearchText;

    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.nameSearch = v ?? '';
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });

    this.buildYearOptions();
    this.loadLookups();
    this.load();
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private buildYearOptions(): void {
    const current = new Date().getFullYear();
    this.yearOptions = [];
    for (let y = current + 1; y >= current - 3; y--) {
      this.yearOptions.push({ label: String(y), value: y });
    }
  }

  private loadLookups(): void {
    forkJoin({
      projects: this.projectService.getAllProjects().pipe(catchError(() => of([] as Project[]))),
      employees: this.employeeService.getAllEmployees().pipe(catchError(() => of([] as Employee[])))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projects, employees }) => {
        this.projects = (projects || []).filter(p => p.isActive !== false);
        this.projectFilterOptions = [
          { label: 'All projects', value: null },
          ...this.projects.map(p => ({ label: p.name, value: p.id }))
        ];
        this.employees = (employees || []).filter(e => e.isActive !== false);
        this.buildServiceGroupOptions();
      });
  }

  private buildServiceGroupOptions(): void {
    const seen = new Map<string, string>();
    for (const e of this.employees) {
      if (e.serviceGroupId && e.serviceGroupName && !seen.has(e.serviceGroupId)) {
        seen.set(e.serviceGroupId, e.serviceGroupName);
      }
    }
    this.serviceGroupOptions = [
      { label: 'All service groups', value: null },
      ...Array.from(seen.entries())
        .map(([value, label]) => ({ label, value }))
        .sort((x, y) => x.label.localeCompare(y.label))
    ];
  }

  private filters(): OutingFilters {
    return {
      year: this.selectedYear,
      projectId: this.filterProjectId ?? undefined
    };
  }

  load(): void {
    this.loading = true;
    forkJoin({
      outings: this.outingService.getAll(this.filters()).pipe(catchError(() => of([] as Outing[]))),
      report: this.outingService.getReport(this.filters()).pipe(catchError(() => of(null)))
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ outings, report }) => {
          this.outings = outings;
          this.report = report;
          this.loading = false;
        },
        error: () => { this.loading = false; }
      });
  }

  onFilterChange(): void {
    this.load();
  }

  hasActiveFilters(): boolean {
    return this.filterProjectId != null || this.filterServiceGroupId != null
      || this.countMode !== 'participated' || !!this.nameSearch.trim()
      || this.selectedYear !== new Date().getFullYear();
  }

  clearFilters(): void {
    this.selectedYear = new Date().getFullYear();
    this.filterProjectId = null;
    this.filterServiceGroupId = null;
    this.countMode = 'participated';
    this.nameSearch = '';
    this.pageHeaderService.setSearchText('');
    this.load();
  }

  // Only completed outings carry meaningful participation; a plan or a cancellation does not.
  private get doneOutings(): Outing[] {
    return this.outings.filter(o => o.status === OutingStatus.Done);
  }

  private monthOf(o: Outing): number {
    return new Date(o.plannedDate).getMonth() + 1;
  }

  // Per-month headline figures across the whole (filtered) set.
  get monthSummaries(): MonthSummary[] {
    return this.months.map(m => {
      const inMonth = this.outings.filter(o => this.monthOf(o) === m);
      const done = inMonth.filter(o => o.status === OutingStatus.Done);
      const invited = done.reduce((s, o) => s + o.participants.length, 0);
      const participated = done.reduce((s, o) => s + o.participants.filter(p => p.attended).length, 0);
      return {
        month: m,
        label: this.monthLabels[m - 1],
        outingCount: inMonth.length,
        invited,
        participated,
        absent: invited - participated,
        budget: inMonth.reduce((s, o) => s + (o.budget ?? 0), 0),
        spend: inMonth.reduce((s, o) => s + (o.actualSpend ?? 0), 0),
        participationRate: invited > 0 ? Math.round((participated / invited) * 1000) / 10 : 0
      };
    });
  }

  // Grid rows: one per employee appearing in the filtered set, plus a Total rollup.
  get reportRows(): OutingReportRow[] {
    const search = this.nameSearch.trim().toLowerCase();
    const byEmployee = new Map<string, OutingReportRow>();

    for (const o of this.doneOutings) {
      const month = this.monthOf(o);
      for (const p of o.participants) {
        if (this.countMode === 'participated' && !p.attended) {
          continue;
        }

        let row = byEmployee.get(p.employeeId);
        if (!row) {
          row = {
            rowType: 'employee',
            employeeId: p.employeeId,
            employeeName: p.employeeName,
            serviceGroupId: p.serviceGroupId,
            serviceGroupName: p.serviceGroupName,
            byMonth: {},
            yearTotal: 0
          };
          byEmployee.set(p.employeeId, row);
        }
        row.byMonth[month] = (row.byMonth[month] ?? 0) + 1;
        row.yearTotal++;
      }
    }

    const rows = Array.from(byEmployee.values())
      .filter(r => !this.filterServiceGroupId || r.serviceGroupId === this.filterServiceGroupId)
      .filter(r => !search || r.employeeName.toLowerCase().includes(search))
      .sort((x, y) => y.yearTotal - x.yearTotal || x.employeeName.localeCompare(y.employeeName));

    if (!rows.length) {
      return rows;
    }

    // Total row closes the grid, mirroring the Availability Report.
    const total: OutingReportRow = {
      rowType: 'total',
      employeeId: 'total',
      employeeName: 'Total',
      byMonth: {},
      yearTotal: 0
    };
    for (const r of rows) {
      for (const m of this.months) {
        const v = r.byMonth[m] ?? 0;
        if (v) {
          total.byMonth[m] = (total.byMonth[m] ?? 0) + v;
        }
      }
      total.yearTotal += r.yearTotal;
    }
    return [...rows, total];
  }

  get employeeRowCount(): number {
    return this.reportRows.filter(r => r.rowType === 'employee').length;
  }

  get totalOutingsInYear(): number {
    return this.outings.length;
  }

  get totalDoneInYear(): number {
    return this.doneOutings.length;
  }

  // Overall participated / invited across the year, in percent.
  get overallParticipationRate(): number {
    const invited = this.doneOutings.reduce((s, o) => s + o.participants.length, 0);
    const attended = this.doneOutings.reduce((s, o) => s + o.participants.filter(p => p.attended).length, 0);
    return invited > 0 ? Math.round((attended / invited) * 1000) / 10 : 0;
  }

  get busiestMonthLabel(): string {
    const list = this.monthSummaries;
    const best = list.reduce((x, y) => (y.outingCount > x.outingCount ? y : x), list[0]);
    return best && best.outingCount > 0 ? best.label : '—';
  }

  cellValue(row: OutingReportRow, month: number): number {
    return row.byMonth[month] ?? 0;
  }

  // Zero reads as a dash rather than a wall of 0s — same treatment as the Availability Report.
  formatValue(v: number): string {
    return v > 0 ? String(v) : '—';
  }

  monthHeaderTooltip(m: MonthSummary): string {
    if (!m.outingCount) {
      return m.label + ': no outings';
    }
    const nl = String.fromCharCode(10);
    const plural = m.outingCount === 1 ? '' : 's';
    return m.label + ': ' + m.outingCount + ' outing' + plural
      + nl + m.participated + ' participated of ' + m.invited + ' invited (' + m.participationRate + '%)'
      + nl + 'Budget ' + m.budget.toLocaleString() + ' | Spend ' + m.spend.toLocaleString();
  }

  cellTooltip(row: OutingReportRow, m: number): string {
    const v = this.cellValue(row, m);
    const label = this.monthLabels[m - 1];
    if (row.rowType === 'total') {
      const what = this.countMode === 'invited' ? 'invitations' : 'participations';
      return label + ': ' + v + ' total ' + what;
    }
    if (!v) {
      return row.employeeName + ' — no outings in ' + label;
    }
    const nl = String.fromCharCode(10);
    const names = this.doneOutings
      .filter(o => this.monthOf(o) === m)
      .filter(o => o.participants.some(p => p.employeeId === row.employeeId
        && (this.countMode === 'invited' || p.attended)))
      .map(o => o.eventName);
    return row.employeeName + ' — ' + label + nl + names.join(nl);
  }

  exportToExcel(): void {
    const rows = this.reportRows;
    if (!rows.length) {
      this.messageService.add({ severity: 'warn', summary: 'Nothing to export', detail: 'No rows for the current filters.' });
      return;
    }

    const data = rows.map(r => {
      const rec: Record<string, string | number> = {
        'Employee': r.employeeName,
        'Service Group': r.serviceGroupName ?? '-'
      };
      for (const m of this.months) {
        rec[this.monthLabels[m - 1]] = this.cellValue(r, m);
      }
      rec['Year Total'] = r.yearTotal;
      return rec;
    });

    const wb = XLSX.utils.book_new();

    // Sheet 1 — the on-screen grid: employee x month.
    const ws = XLSX.utils.json_to_sheet(data);
    autoFitColumns(ws, data);
    addAutoFilter(ws);
    XLSX.utils.book_append_sheet(wb, ws,
      this.countMode === 'invited' ? 'Invited by month' : 'Participation by month');

    // Sheet 2 — the monthly event summary, so the file also answers "what happened each month"
    // without the reader having to reconstruct it from the grid.
    const summary = this.monthSummaries.map(m => ({
      'Month': m.label + ' ' + this.selectedYear,
      'Outings': m.outingCount,
      'Invited': m.invited,
      'Participated': m.participated,
      'Not attended': m.absent,
      'Participation %': m.participationRate,
      'Budget': m.budget,
      'Actual spend': m.spend
    }));
    const wsSummary = XLSX.utils.json_to_sheet(summary);
    autoFitColumns(wsSummary, summary);
    addAutoFilter(wsSummary);
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Monthly summary');

    // Sheet 3 — every outing in the period, one row each.
    const events = this.outings
      .slice()
      .sort((x, y) => +new Date(x.plannedDate) - +new Date(y.plannedDate))
      .map(o => ({
        'Event': o.eventName,
        'Project': o.projectName,
        'PM': o.pmName,
        'Date': new Date(o.plannedDate).toLocaleDateString(),
        'Venue': o.location ?? '-',
        'Status': o.status === OutingStatus.Done ? 'Done'
          : (o.status === OutingStatus.Cancelled ? 'Cancelled' : 'Planning'),
        'Invited': o.participants.length,
        'Participated': o.participants.filter(p => p.attended).length,
        'Not attended': o.participants.filter(p => !p.attended).length,
        'Budget': o.budget ?? 0,
        'Actual spend': o.actualSpend ?? 0
      }));
    if (events.length) {
      const wsEvents = XLSX.utils.json_to_sheet(events);
      autoFitColumns(wsEvents, events);
      addAutoFilter(wsEvents);
      XLSX.utils.book_append_sheet(wb, wsEvents, 'Events');
    }

    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      'Outing_Report_' + this.selectedYear + '.xlsx');
  }
}
